import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Typography, Card, Button, Tooltip, Table, Input, Tag, theme, Spin, Empty, Drawer, Descriptions, Collapse, Modal, message, Form, Select, Space, Badge, Checkbox, Grid } from "antd";
import { LoadingOutlined, ReloadOutlined, SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, UnorderedListOutlined, FolderOutlined, FolderOpenOutlined, ShoppingOutlined, AppstoreOutlined, FileTextOutlined, RightOutlined, DownOutlined, FilterOutlined } from "@ant-design/icons";
import {
  closestCorners,
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useAppDispatch } from "../../hooks/reduxHooks.js";
import {
  getProductListApi,
  getProductHierarchyApi,
  syncProductOverviewApi,
  manageProductHierarchyApi,
  moveProductHierarchyNodeApi,
  deleteProductHierarchyNodeApi,
} from "../../services/products.service.js";
import { useSearchParams } from "react-router-dom";
import DataTablePagination from "../../components/common/dataTablePagination.jsx";
import TagsEditor from "./TagEditor.jsx";
import { debounce } from "lodash";
import Highlighter from "react-highlight-words";
import {
  cloneHierarchyNodes,
  countHierarchyNestedItems,
  findHierarchyNodeMeta,
  formatHierarchyNodeName,
  getHierarchyNodeChildren,
  getHierarchyNodeDetailFields,
  getHierarchyNodeKey,
  getHierarchyNodeSearchText,
  isHierarchyProductNode,
  moveHierarchyNodeInTree,
} from "../../utils/productHierarchy.utils";
const { Title } = Typography;
const { TextArea } = Input;

const DROP_TYPE = {
  INTO: "into",
  ROOT_END: "root-end",
};

const hierarchyCollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }

  return closestCorners(args);
};

const toHashKey = (value) => String(value);

const findTreeNodeByKey = (nodes, nodeKey) => {
  const targetId = toHashKey(nodeKey);
  for (const node of nodes || []) {
    if (toHashKey(getHierarchyNodeKey(node?.data)) === targetId) {
      return node;
    }
    const child = findTreeNodeByKey(node?.children || [], nodeKey);
    if (child) return child;
  }
  return null;
};

const DropZone = ({ id, data, activeDragNodeId, style, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id, data });
  const isDragging = Boolean(activeDragNodeId);
  const isHighlighted = isOver && isDragging;
  const hasChildren = React.Children.count(children) > 0;
  const baseHeight = style?.height ?? 8;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        height: hasChildren ? style?.height : isDragging ? baseHeight : 0,
        minHeight: hasChildren ? (style?.minHeight ?? 24) : undefined,
        borderRadius: style?.borderRadius ?? 8,
        transition: "all 0.15s ease",
        backgroundColor: isHighlighted
          ? "#e6f4ff"
          : style?.backgroundColor || "transparent",
        boxShadow: isHighlighted
          ? "0 0 0 2px #91caff inset"
          : style?.boxShadow,
      }}
    >
      {children}
    </div>
  );
};

const DraggableDroppableNode = ({
  nodeId,
  disabled = false,
  activeDragNodeId,
  canHoverExpand = false,
  hoverExpandDelay = 1500,
  onHoverExpand,
  onDropHoverChange,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef: setDragNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `drag-${nodeId}`,
    data: { type: "node", nodeId },
    disabled,
  });

  const { setNodeRef: setDropNodeRef, isOver } = useDroppable({
    id: `drop-into-${nodeId}`,
    data: { type: "drop", dropType: DROP_TYPE.INTO, targetId: nodeId },
  });
  const hoverTimerRef = useRef(null);
  const hoverExpandedRef = useRef(false);

  const setNodeRef = useCallback(
    (element) => {
      setDragNodeRef(element);
      setDropNodeRef(element);
    },
    [setDragNodeRef, setDropNodeRef]
  );

  useEffect(() => {
    const shouldScheduleExpand =
      isOver &&
      Boolean(activeDragNodeId) &&
      activeDragNodeId !== nodeId &&
      canHoverExpand &&
      typeof onHoverExpand === "function";

    if (shouldScheduleExpand) {
      if (!hoverTimerRef.current && !hoverExpandedRef.current) {
        hoverTimerRef.current = setTimeout(() => {
          onHoverExpand();
          hoverExpandedRef.current = true;
          hoverTimerRef.current = null;
        }, hoverExpandDelay);
      }
    } else {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      if (!isOver) {
        hoverExpandedRef.current = false;
      }
    }

    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }, [
    activeDragNodeId,
    canHoverExpand,
    hoverExpandDelay,
    isOver,
    nodeId,
    onHoverExpand,
  ]);

  useEffect(() => {
    if (typeof onDropHoverChange === "function") {
      onDropHoverChange(nodeId, isOver);
    }
  }, [isOver, nodeId, onDropHoverChange]);

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.2 : 1,
    cursor: disabled ? "default" : "grab",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow:
      isOver && activeDragNodeId && activeDragNodeId !== nodeId
        ? "0 0 0 2px #91caff inset"
        : undefined,
    touchAction: "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(disabled ? {} : attributes)}
      {...(disabled ? {} : listeners)}
    >
      {children}
    </div>
  );
};

const ProductComponent = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // Initialize state from URL parameters
  const [productsList, setProductsList] = useState({ products: [], total: 0, page_number: 1, page_size: 10 });
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get("pageSize")) || 10);
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get("pageNumber")) || 1);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [loading, setLoading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(searchParams.get("categories")?.split(',').filter(Boolean) || []);
  const [hierarchy, setHierarchy] = useState([]);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [hierarchySearch, setHierarchySearch] = useState("");
  const [syncLoading, setSyncLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Hierarchy management states
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [deleteProductWithNode, setDeleteProductWithNode] = useState(false);
  const [editingNodeKey, setEditingNodeKey] = useState(null);
  const [addingToNodeKey, setAddingToNodeKey] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', description: '', tags: [] });
  const [addFormData, setAddFormData] = useState({ name: '', description: '', tags: [] });
  const [selectedNode, setSelectedNode] = useState(null);
  const [rootModalVisible, setRootModalVisible] = useState(false);
  const [rootFormData, setRootFormData] = useState({ name: '', description: '', tags: [] });
  const [activeDragNodeId, setActiveDragNodeId] = useState(null);
  const [activeDragPreview, setActiveDragPreview] = useState(null);
  const [hoveredDropParentNodeId, setHoveredDropParentNodeId] = useState(null);
  const [reorderSaving, setReorderSaving] = useState(false);
  const [form] = Form.useForm();
  const hierarchyRef = useRef();
  const addFormRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 3 },
    })
  );

  useEffect(() => {
    setLoading(true);

    const handler = debounce(() => {
      setPageNumber(1);
      setDebouncedSearch(searchQuery);
      setLoading(false);
    }, 500);

    handler();

    return () => {
      handler.cancel();
    };
  }, [searchQuery]);

  useEffect(() => {
    if (debouncedSearch !== undefined) {
      const params = {
        skip: (pageNumber - 1) * pageSize,
        limit: pageSize,
        Search: debouncedSearch,
      };

      // Ensure category is always an array for the serializer
      if (selectedNode && !isHierarchyProductNode(selectedNode)) {
        params.category = formatHierarchyNodeName(selectedNode.name);
      }

      if (selectedCategories.length > 0) {
        params.category = selectedCategories;
      }

      if (selectedNode && isHierarchyProductNode(selectedNode)) {
        if (selectedNode.product_id) {
          params.product_id = selectedNode.product_id;
        } else if (selectedNode.code) {
          params.Search = selectedNode.code;
        }
      }

      dispatch(
        getProductListApi({
          params,
          successCallback: (response) => {
            setProductsList(response);
          },
          failureCallback: (errors) => {
            console.error("API Error:", errors);
          },
        })
      );
    }
  }, [dispatch, pageNumber, pageSize, refreshTrigger, debouncedSearch, selectedNode, selectedCategories]);

  const loadHierarchy = useCallback((options = {}) => {
    const { preserveScroll, showLoader } = typeof options === "boolean"
      ? { preserveScroll: options, showLoader: true }
      : { preserveScroll: false, showLoader: true, ...options };
    const scrollToRestore = preserveScroll && hierarchyRef.current ? hierarchyRef.current.scrollTop : 0;
    if (showLoader) {
      setHierarchyLoading(true);
    }
    dispatch(
      getProductHierarchyApi({
        successCallback: (response) => {
          setHierarchy(Array.isArray(response?.hierarchy) ? response.hierarchy : []);
        },
        failureCallback: (errors) => {
          console.log(errors);
        },
        finalCallback: () => {
          if (showLoader) {
            setHierarchyLoading(false);
          }
          // Restore scroll position after DOM updates
          if (preserveScroll && scrollToRestore > 0) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (hierarchyRef.current) {
                  hierarchyRef.current.scrollTop = scrollToRestore;
                }
              });
            });
          }
        },
      })
    );
  }, [dispatch]);

  const handleAddHierarchyNode = (nodeKey, parentNode) => {
    setAddingToNodeKey(nodeKey);
    setAddFormData({ name: '', description: '', tags: [] });
    // Scroll to the add form after it renders
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (addFormRef.current) {
          addFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    });
  };

  const handleEditHierarchyNode = (nodeKey, node) => {
    setEditingNodeKey(nodeKey);
    setEditFormData({
      name: formatHierarchyNodeName(node.name),
      description: node.description || "",
      tags: node.tags || [],
    });
  };

  const handleCancelEdit = () => {
    setEditingNodeKey(null);
    setEditFormData({ name: '', description: '', tags: [] });
  };

  const handleCancelAdd = () => {
    setAddingToNodeKey(null);
    setAddFormData({ name: '', description: '', tags: [] });
  };

  const handleDeleteHierarchyNode = (node) => {
    setNodeToDelete(node);
    setDeleteProductWithNode(false);
    setDeleteConfirmVisible(true);
  };

  const handleConfirmDelete = () => {
    if (nodeToDelete) {
      dispatch(
        deleteProductHierarchyNodeApi({
          hash_id: nodeToDelete.hash_id || getHierarchyNodeKey(nodeToDelete),
          delete_product: deleteProductWithNode,
          finalCallback: () => { },
          successCallback: (response) => {
            message.success(
              deleteProductWithNode
                ? "Hierarchy node and product deleted successfully"
                : "Hierarchy node deleted successfully"
            );
            // Reload hierarchy from server with scroll preservation
            loadHierarchy(true);
            setDeleteConfirmVisible(false);
            setNodeToDelete(null);
            setDeleteProductWithNode(false);
            // Refresh the product list
            setRefreshTrigger(prev => !prev);
          },
          failureCallback: (error) => {
            message.error("Failed to delete hierarchy node");
            console.error(error);
          },
        })
      );
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmVisible(false);
    setNodeToDelete(null);
    setDeleteProductWithNode(false);
  };

  const handleSaveEdit = (node) => {
    if (!editFormData.name.trim()) {
      message.error('Product name is required');
      return;
    }

    const payload = {
      action: "update",
      hash_id: node.hash_id || getHierarchyNodeKey(node),
      name: editFormData.name,
      description: editFormData.description || "",
      tags: editFormData.tags || [],
      product_id: node.product_id || null,
    };

    dispatch(
      manageProductHierarchyApi({
        data: payload,
        finalCallback: () => { },
        successCallback: (response) => {
          message.success("Product updated successfully");
          setEditingNodeKey(null);
          setEditFormData({ name: '', description: '', tags: [] });
          const oldName = formatHierarchyNodeName(node.name);
          const newName = editFormData.name;
          // Refresh hierarchy silently to avoid visual blinking
          loadHierarchy({ preserveScroll: true, showLoader: false });
          // Update the selectedNode if it's the same node
          if (
            selectedNode &&
            getHierarchyNodeKey(selectedNode) === getHierarchyNodeKey(node)
          ) {
            setSelectedNode({
              ...selectedNode,
              name: newName,
              description: editFormData.description || "",
              tags: editFormData.tags || []
            });
          }
          // Update the products in the list immediately
          if (node.product_id) {
            // Update product code if it's a product
            setProductsList(prev => ({
              ...prev,
              products: prev.products.map(product =>
                product._id === node.product_id
                  ? { ...product, code: newName }
                  : product
              )
            }));
          } else {
            // Update category in products if it's a category
            setProductsList(prev => ({
              ...prev,
              products: prev.products.map(product => ({
                ...product,
                category: Array.isArray(product.category)
                  ? product.category.map(cat => cat === oldName ? newName : cat)
                  : product.category === oldName ? newName : product.category
              }))
            }));
          }
        },
        failureCallback: (error) => {
          message.error("Failed to update product");
          console.error(error);
        },
      })
    );
  };

  const handleSaveAdd = (parentNode) => {
    if (!addFormData.name.trim()) {
      message.error('Product name is required');
      return;
    }

    const payload = {
      action: "add",
      parent_hash_id: parentNode.hash_id || getHierarchyNodeKey(parentNode),
      name: addFormData.name,
      description: addFormData.description || "",
      tags: addFormData.tags || [],
      product_id: null, // New hierarchy items don't have a product_id initially
    };

    dispatch(
      manageProductHierarchyApi({
        data: payload,
        finalCallback: () => { },
        successCallback: (response) => {
          message.success("Product added successfully");
          setAddingToNodeKey(null);
          setAddFormData({ name: '', description: '', tags: [] });
          // Refresh hierarchy silently to avoid visual blinking
          loadHierarchy({ preserveScroll: true, showLoader: false });
          // Refresh the product list to show any updates
          setRefreshTrigger(prev => !prev);
        },
        failureCallback: (error) => {
          message.error("Failed to add product");
          console.error(error);
        },
      })
    );
  };

  const handleRootModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        action: "add",
        parent_hash_id: null,
        name: values.name,
        description: values.description || "",
        tags: values.tags || [],
        product_id: null, // Root categories don't have a product_id initially
      };

      dispatch(
        manageProductHierarchyApi({
          data: payload,
          finalCallback: () => { },
          successCallback: (response) => {
            message.success("Root category added successfully");
            setRootModalVisible(false);
            setRootFormData({ name: '', description: '', tags: [] });
            form.resetFields();
            // Refresh hierarchy silently to avoid visual blinking
            loadHierarchy({ preserveScroll: true, showLoader: false });
            // Refresh the product list to show any updates
            setRefreshTrigger(prev => !prev);
          },
          failureCallback: (error) => {
            message.error("Failed to add category");
            console.error(error);
          },
        })
      );
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleRootModalCancel = () => {
    setRootModalVisible(false);
    setRootFormData({ name: '', description: '', tags: [] });
    form.resetFields();
  };

  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);

  // Initialize selectedNode from URL params
  useEffect(() => {
    const selectedNodeHash = searchParams.get("selectedNode");
    if (selectedNodeHash && hierarchy.length > 0) {
      const findNode = (nodes) => {
        for (const node of nodes) {
          if (getHierarchyNodeKey(node) === selectedNodeHash) {
            return node;
          }
          const children = getHierarchyNodeChildren(node);
          if (children.length > 0) {
            const found = findNode(children);
            if (found) return found;
          }
        }
        return null;
      };
      const node = findNode(hierarchy);
      if (node) {
        setSelectedNode(node);
      }
    }
  }, [hierarchy, searchParams]);

  // Update URL parameters when filter state changes
  const currentSearch = searchParams.toString();

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (searchQuery) params.set("search", searchQuery);
    else params.delete("search");

    if (pageSize !== 10) params.set("pageSize", pageSize.toString());
    else params.delete("pageSize");

    if (pageNumber !== 1) params.set("pageNumber", pageNumber.toString());
    else params.delete("pageNumber");

    if (selectedNode) params.set("selectedNode", getHierarchyNodeKey(selectedNode));
    else params.delete("selectedNode");

    if (selectedCategories.length > 0) params.set("categories", selectedCategories.join(','));
    else params.delete("categories");

    const newSearch = params.toString();

    if (newSearch !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
  }, [searchQuery, pageSize, pageNumber, selectedNode, selectedCategories, currentSearch, setSearchParams, searchParams]);

  // Restore scroll position after hierarchy updates
  useEffect(() => {
    if (scrollPosition > 0 && hierarchyRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (hierarchyRef.current) {
            hierarchyRef.current.scrollTop = scrollPosition;
            setScrollPosition(0);
          }
        });
      });
    }
  }, [hierarchy, scrollPosition]);

  const columns = [
    {
      title: "Product",
      key: "product",
      width: isMobile ? 260 : 360,
      render: (_, record) => {
        const categories = Array.isArray(record?.category)
          ? record.category
          : record?.category
            ? [record.category]
            : [];
        return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: isMobile ? 240 : "auto" }}>
          <Typography.Text strong style={{ fontSize: 14, color: token.colorTextHeading }}>
            <Highlighter
              highlightClassName="YourHighlightClass"
              searchWords={[searchQuery]}
              autoEscape={true}
              textToHighlight={record?.name || ""}
              highlightStyle={{ backgroundColor: "#FFD700", padding: "2px 4px", borderRadius: "4px" }}
            />
          </Typography.Text>
          <Typography.Text
            style={{
              fontSize: 12,
              fontFamily: "monospace",
              color: token.colorPrimaryText,
              background: token.colorFillAlter,
              padding: "4px 8px",
              borderRadius: "6px",
              border: `1px solid ${token.colorBorderSecondary}`,
              display: "inline-block",
              width: "fit-content",
            }}
          >
            <Highlighter
              highlightClassName="YourHighlightClass"
              searchWords={[searchQuery]}
              autoEscape={true}
              textToHighlight={record?.code || ""}
              highlightStyle={{ backgroundColor: "#FFD700", padding: "2px 4px", borderRadius: "4px" }}
            />
          </Typography.Text>
          {isMobile && categories.length > 0 && (
            <Space size={[0, 4]} wrap>
              {categories.slice(0, 2).map((cat, index) => (
                <Tag
                  key={`${cat}-${index}`}
                  color="processing"
                  style={{ borderRadius: 12, marginInlineEnd: 4 }}
                >
                  {cat}
                </Tag>
              ))}
              {categories.length > 2 && (
                <Tag style={{ borderRadius: 12, marginInlineEnd: 0 }}>+{categories.length - 2}</Tag>
              )}
            </Space>
          )}
          {isMobile && (
            <Typography.Text style={{ fontSize: 12, color: token.colorTextDescription }}>
              Rs. {record.price}
              {record.default_discount_amount > 0 ? `  (-Rs. ${record.default_discount_amount})` : ""}
            </Typography.Text>
          )}
        </div>
      )},
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 150,
      responsive: ["md"],
      render: (category) => {
        const categories = Array.isArray(category) ? category : category ? [category] : [];
        return (
          <Space size={[0, 4]} wrap style={{ maxWidth: 150 }}>
            {categories.map((cat, index) => (
              <Tag
                key={`${cat}-${index}`}
                color="processing"
                style={{ borderRadius: 12 }}
              >
                <Highlighter
                  highlightClassName="YourHighlightClass"
                  searchWords={[searchQuery]}
                  autoEscape={true}
                  textToHighlight={cat}
                  highlightStyle={{ backgroundColor: "#FFD700", padding: "0 2px", borderRadius: "4px" }}
                />
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      width: isMobile ? 280 : 220,
      render: (_tags, record) => {
        const tags = _tags ? _tags.split(",") : [];
        return (
          <div style={{ maxWidth: isMobile ? "none" : 220, minWidth: isMobile ? 260 : "auto", whiteSpace: "normal", wordBreak: "break-word" }}>
            <TagsEditor
              row={record}
              value={Array.isArray(tags) ? tags : []}
              searchQuery={searchQuery}
              onChange={(newTags) => {
                setProductsList((prev) => ({
                  ...prev,
                  products: prev.products.map((p) =>
                    p._id === record._id ? { ...p, tags: newTags } : p
                  ),
                }));
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Price",
      key: "price",
      width: 120,
      responsive: ["md"],
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Typography.Text
            strong
            style={{ fontSize: 14, color: token.colorTextHeading }}
          >
            Rs. {record.price}
          </Typography.Text>
          {record.default_discount_amount > 0 && (
            <Tag color="success" style={{ margin: 0, borderRadius: 12 }}>
              -Rs. {record.default_discount_amount}
            </Tag>
          )}
        </Space>
      ),
    },
  ];

  const hierarchyTreeData = useMemo(() => {
    const search = hierarchySearch.trim().toLowerCase();

    const buildNodes = (items, prefix) =>
      (items || [])
        .map((item, index) => {
          const title = formatHierarchyNodeName(item?.name);
          const key = `${prefix}-${index}`;
          const childNodes = buildNodes(getHierarchyNodeChildren(item), key);
          const matched =
            !search || getHierarchyNodeSearchText(item).includes(search);

          if (!matched && childNodes.length === 0) return null;

          return {
            title,
            key,
            data: item,
            children: childNodes,
          };
        })
        .filter(Boolean);

    return buildNodes(hierarchy, "node");
  }, [hierarchy, hierarchySearch]);

  // Toggle expand/collapse for a node
  const toggleExpand = (key) => {
    setExpandedKeys(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const handleDragStart = (event) => {
    const activeNodeId = event?.active?.data?.current?.nodeId;
    setActiveDragNodeId(activeNodeId || null);
    setHoveredDropParentNodeId(null);

    if (!activeNodeId) {
      setActiveDragPreview(null);
      return;
    }

    const activeTreeNode = findTreeNodeByKey(hierarchyTreeData, activeNodeId);
    if (!activeTreeNode) {
      setActiveDragPreview(null);
      return;
    }

    const hasChildren =
      Array.isArray(activeTreeNode.children) && activeTreeNode.children.length > 0;

    // Collapse dragged node when drag starts.
    setExpandedKeys((prev) => prev.filter((key) => key !== activeTreeNode.key));
    setActiveDragPreview({
      title: activeTreeNode.title || "Moving item",
      hasProductId: isHierarchyProductNode(activeTreeNode.data),
      hasChildren,
    });
  };

  const handleDragCancel = () => {
    setActiveDragNodeId(null);
    setActiveDragPreview(null);
    setHoveredDropParentNodeId(null);
  };

  const handleDragEnd = ({ active, over }) => {
    if (reorderSaving) {
      setActiveDragNodeId(null);
      setActiveDragPreview(null);
      return;
    }

    const activeNodeId = active?.data?.current?.nodeId;
    const dropData = over?.data?.current;
    setActiveDragNodeId(null);
    setActiveDragPreview(null);
    setHoveredDropParentNodeId(null);

    if (!activeNodeId || !dropData || dropData.type !== "drop") {
      return;
    }

    const sourceMeta = findHierarchyNodeMeta(hierarchy, activeNodeId);
    const targetMeta =
      dropData.dropType === DROP_TYPE.ROOT_END
        ? null
        : findHierarchyNodeMeta(hierarchy, dropData.targetId);
    const sourceName = formatHierarchyNodeName(sourceMeta?.node?.name) || "Current item";
    let destinationName = "Root level";
    if (dropData.dropType === DROP_TYPE.INTO) {
      destinationName = formatHierarchyNodeName(targetMeta?.node?.name) || "Selected item";
    }

    const previousHierarchy = cloneHierarchyNodes(hierarchy);
    const nextHierarchy = cloneHierarchyNodes(hierarchy);
    const { moved, invalidCycle, move } = moveHierarchyNodeInTree({
      tree: nextHierarchy,
      activeNodeKey: activeNodeId,
      dropData,
      dropTypes: DROP_TYPE,
    });

    if (invalidCycle) {
      message.warning("Parent cannot move to its own child.");
      return;
    }

    if (moved) {
      Modal.confirm({
        title: "Confirm move",
        content: `Are you sure you want to move "${sourceName}" to "${destinationName}"?`,
        okText: "Move",
        cancelText: "Cancel",
        onOk: () => {
          setHierarchy(nextHierarchy);
          setReorderSaving(true);
          dispatch(
            moveProductHierarchyNodeApi({
              data: {
                hash_id: move?.hash_id,
                parent_hash_id: move?.parent_hash_id,
                position: move?.position,
                previous_parent_hash_id: move?.previous_parent_hash_id,
                previous_position: move?.previous_position,
              },
              finalCallback: () => setReorderSaving(false),
              successCallback: () => {},
              failureCallback: (error) => {
                setHierarchy(previousHierarchy);
                message.error(
                  error?.response?.data?.detail ||
                  "Failed to save hierarchy order. Changes reverted."
                );
              },
            })
          );
        },
      });
    }
  };

  const handleDropHoverChange = useCallback((hoveredNodeId, isOverNode) => {
    if (!isOverNode) {
      setHoveredDropParentNodeId((prev) =>
        prev === hoveredNodeId ? null : prev
      );
      return;
    }
    setHoveredDropParentNodeId(hoveredNodeId);
  }, []);

  // Render the redesigned tree node
  const renderTreeNode = (node, level = 0, isLast = false) => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const hasProductId = isHierarchyProductNode(node.data);
    const isEditing = editingNodeKey === node.key;
    const isAddingChild = addingToNodeKey === node.key;
    const dataNodeKey = getHierarchyNodeKey(node.data);
    const nodeId = toHashKey(dataNodeKey);
    const dragDisabled =
      reorderSaving || isEditing || isAddingChild || !dataNodeKey;
    const isExpanded = expandedKeys.includes(node.key);
    const isSelected =
      getHierarchyNodeKey(selectedNode) === getHierarchyNodeKey(node.data);
    const itemName = formatHierarchyNodeName(node?.data?.name) || node.title;
    const descendantCount = countHierarchyNestedItems(node.data);
    const detailFields = getHierarchyNodeDetailFields(node.data);
    const isDropParentHighlighted =
      Boolean(activeDragNodeId) &&
      activeDragNodeId !== nodeId &&
      hoveredDropParentNodeId === nodeId;

    // Level-based color theming
    const levelColors = [
      { bg: '#f0f5ff', border: '#adc6ff', icon: '#2f54eb', accent: '#2f54eb' },
      { bg: '#f6ffed', border: '#b7eb8f', icon: '#52c41a', accent: '#389e0d' },
      { bg: '#fff7e6', border: '#ffd591', icon: '#fa8c16', accent: '#d46b08' },
      { bg: '#fff1f0', border: '#ffa39e', icon: '#f5222d', accent: '#cf1322' },
      { bg: '#f9f0ff', border: '#d3adf7', icon: '#722ed1', accent: '#531dab' },
    ];
    const colorScheme = levelColors[level % levelColors.length];

    const hasTags = Array.isArray(node.data?.tags) && node.data.tags.length > 0;
    const hasDescription = !!node.data?.description;
    const hasDetails =
      hasDescription || hasTags || detailFields.length > 0;
    const showDetails = isExpanded && hasDetails;
    const canAutoExpandOnHover =
      hasChildren && !isExpanded && !isEditing && !isAddingChild;

    return (
      <div key={node.key} className="hierarchy-tree-node" style={{ marginBottom: level === 0 ? 6 : 3 }}>
        {/* Unified node card */}
        <DraggableDroppableNode
          nodeId={nodeId}
          disabled={dragDisabled}
          activeDragNodeId={activeDragNodeId}
          canHoverExpand={canAutoExpandOnHover}
          onDropHoverChange={handleDropHoverChange}
          onHoverExpand={() =>
            setExpandedKeys((prev) =>
              prev.includes(node.key) ? prev : [...prev, node.key]
            )
          }
        >
          <div
            className={`hierarchy-tree-card ${isSelected ? 'hierarchy-tree-card-selected' : ''} ${showDetails || isEditing ? 'hierarchy-tree-card-expanded' : ''}`}
            style={{
              borderRadius: 8,
              transition: 'all 0.2s ease',
              background: isDropParentHighlighted
                ? '#e6f4ff'
                : isSelected
                  ? colorScheme.bg
                  : '#fff',
              border: isDropParentHighlighted
                ? '1px solid #91caff'
                : isSelected
                  ? `1px solid ${colorScheme.border}`
                  : '1px solid #f0f0f0',
              boxShadow: isDropParentHighlighted
                ? '0 0 0 1px rgba(24, 144, 255, 0.18), 0 4px 12px rgba(24, 144, 255, 0.12)'
                : undefined,
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
              if (!isSelected && !isDropParentHighlighted) {
                e.currentTarget.style.borderColor = '#d9d9d9';
                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSelected && !isDropParentHighlighted) {
                e.currentTarget.style.borderColor = '#f0f0f0';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
          {/* Header row */}
          <div
            className="hierarchy-tree-node-header"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '9px 12px',
              cursor: 'pointer',
              gap: 8,
            }}
            onClick={(e) => {
              if (!isEditing && !isAddingChild) {
                toggleExpand(node.key);
              }
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              if (isAddingChild) return;
              if (isEditing) {
                handleCancelEdit();
              } else {
                handleEditHierarchyNode(node.key, node.data);
              }
            }}
          >
            {/* Expand/collapse toggle */}
            {hasChildren ? (
              <div
                onClick={(e) => { e.stopPropagation(); toggleExpand(node.key); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  color: '#8c8c8c',
                }}
                className="hierarchy-tree-expand-btn"
              >
                {isExpanded ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
              </div>
            ) : (
              <div style={{ width: 20, flexShrink: 0 }} />
            )}

            {/* Node icon */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 7,
                background: hasProductId ? '#e6f7ff' : colorScheme.bg,
                border: `1px solid ${hasProductId ? '#91d5ff' : colorScheme.border}`,
                flexShrink: 0,
              }}
            >
              {hasProductId ? (
                <ShoppingOutlined style={{ fontSize: 14, color: '#1890ff' }} />
              ) : hasChildren ? (
                isExpanded ? (
                  <FolderOpenOutlined style={{ fontSize: 14, color: colorScheme.icon }} />
                ) : (
                  <FolderOutlined style={{ fontSize: 14, color: colorScheme.icon }} />
                )
              ) : (
                <FileTextOutlined style={{ fontSize: 14, color: colorScheme.icon }} />
              )}
            </div>

            {/* Node content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {isEditing ? (
                <Input
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  placeholder="Product name"
                  style={{ width: '100%' }}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <Tooltip title={node.title} mouseEnterDelay={0.4}>
                    <span style={{
                      fontWeight: level === 0 ? 600 : 500,
                      fontSize: level === 0 ? 14 : 13,
                      color: '#1f1f1f',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                    }}>
                      {node.title}
                    </span>
                  </Tooltip>
                  {!hasProductId && (
                    <Tag
                      style={{
                        margin: 0,
                        fontSize: 10,
                        lineHeight: '16px',
                        padding: '0 7px',
                        borderRadius: 999,
                        flexShrink: 0,
                      }}
                    >
                      {descendantCount} {descendantCount === 1 ? "Item" : "Items"}
                    </Tag>
                  )}
                  {hasProductId && (
                    <Tag color="blue" style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 5px', flexShrink: 0 }}>
                      Product
                    </Tag>
                  )}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <Space size={8} onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
              {isEditing ? (
                <>
                  <Tooltip title="Save">
                    <Button
                      type="primary"
                      size="small"
                      icon={<SaveOutlined />}
                      onClick={() => handleSaveEdit(node.data)}
                      style={{ background: '#1677ff', borderColor: '#1677ff', color: '#ffffff' }}
                    />
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <Button type="text" size="small" icon={<CloseOutlined />} onClick={handleCancelEdit} style={{ color: '#ff4d4f' }} />
                  </Tooltip>
                </>
              ) : (
                <div className="hierarchy-tree-actions">
                  <Tooltip title="Filter by this">
                    <Button
                      type="text"
                      size="small"
                      icon={<FilterOutlined />}
                      className={isSelected ? "border-primary text-primary" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedNode(node.data);
                        setPageNumber(1);
                        message.info(`Showing products for: ${itemName}`);
                        setDrawerVisible(false);
                      }}
                      style={isSelected ? { color: '#1890ff', fontWeight: 'bold' } : { color: '#595959' }}
                    />
                  </Tooltip>
                  <Tooltip title="Add sub-item">
                    <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => handleAddHierarchyNode(node.key, node.data)} style={{ color: '#1890ff' }} />
                  </Tooltip>
                  <Tooltip title="Edit">
                    <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditHierarchyNode(node.key, node.data)} style={{ color: '#595959' }} />
                  </Tooltip>
                  <Tooltip title="Delete">
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); handleDeleteHierarchyNode(node.data); }} />
                  </Tooltip>
                </div>
              )}
            </Space>
          </div>

          {/* Expanded detail section – inside the same card */}
          {hasDetails && !isEditing && (
            <div
              className={`hierarchy-tree-details ${showDetails ? 'is-expanded' : ''}`}
              aria-hidden={!showDetails}
            >
              <div
                className="hierarchy-tree-details-inner"
                style={{
                  borderTop: '1px solid #f0f0f0',
                  padding: '12px 12px 12px 62px',
                  background: isSelected ? 'transparent' : '#fafbfc',
                }}
              >
                {detailFields
                  .filter((field) => field.label !== "Description" && field.label !== "Tags")
                  .map((field) => (
                    <div key={field.label} style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 14, color: '#262626', fontWeight: 400, display: 'block', marginBottom: 4 }}>
                        {field.label}
                      </label>
                      <div style={{ fontSize: 13, color: '#434343', lineHeight: 1.6 }}>
                        {field.value}
                      </div>
                    </div>
                  ))}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 14, color: '#262626', fontWeight: 400, display: 'block', marginBottom: 4 }}>Description</label>
                  <div
                    style={{ fontSize: 13, color: '#434343', lineHeight: 1.6, cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditHierarchyNode(node.key, node.data);
                    }}
                  >
                    {node.data?.description || "No description"}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 14, color: '#262626', fontWeight: 400, display: 'block', marginBottom: 4 }}>Tags</label>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditHierarchyNode(node.key, node.data);
                    }}
                  >
                    {(Array.isArray(node.data?.tags) ? node.data.tags : []).length > 0 ? (
                      (Array.isArray(node.data?.tags) ? node.data.tags : []).map((tag) => (
                        <Tag
                          key={tag}
                          style={{
                            margin: 0,
                            fontSize: 12,
                            lineHeight: '20px',
                            padding: '1px 8px',
                            borderRadius: 4,
                            background: '#f0f2f5',
                            border: '1px solid #e8e8e8',
                            color: '#595959',
                          }}
                        >
                          {tag}
                        </Tag>
                      ))
                    ) : (
                      <span style={{ fontSize: 12, color: '#8c8c8c' }}>No tags</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit form – inside the same card */}
          {isEditing && (
            <div style={{
              borderTop: '1px solid #f0f0f0',
              padding: '12px 12px 12px 62px',
              background: '#fafbfc',
            }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 14, color: '#262626', fontWeight: 400, display: 'block', marginBottom: 4 }}>Description</label>
                <TextArea
                  rows={2}
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  placeholder="Enter description (optional)"
                  style={{ width: '100%' }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div>
                <label style={{ fontSize: 14, color: '#262626', fontWeight: 400, display: 'block', marginBottom: 4 }}>Tags</label>
                <Select
                  mode="tags"
                  value={editFormData.tags}
                  onChange={(tags) => setEditFormData({ ...editFormData, tags })}
                  placeholder="Enter tags (press Enter to add)"
                  style={{ width: '100%' }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}
          </div>
        </DraggableDroppableNode>

        {/* Children nodes - outside the card */}
        {hasChildren && (
          <div
            className={`hierarchy-tree-children ${isExpanded ? 'is-expanded' : ''}`}
            aria-hidden={!isExpanded}
            style={{
              marginLeft: 22,
              paddingLeft: 20,
              borderLeft: `2px solid ${colorScheme.border}`,
              marginTop: 2,
            }}
          >
            <div className="hierarchy-tree-children-inner">
              {node.children.map((child, idx) =>
                renderTreeNode(child, level + 1, idx === node.children.length - 1)
              )}
            </div>
          </div>
        )}

        {/* Add item form - outside the card */}
        {isAddingChild && (
          <div
            ref={addFormRef}
            style={{
              marginLeft: 54,
              marginTop: 8,
              padding: 16,
              background: 'linear-gradient(135deg, #f6ffed 0%, #fcffe6 100%)',
              borderRadius: 10,
              border: '1px solid #b7eb8f',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <PlusOutlined style={{ color: '#52c41a', fontSize: 14 }} />
              <span style={{ fontWeight: 600, fontSize: 13, color: '#389e0d' }}>
                Add under "{node.title}"
              </span>
              <div style={{ flex: 1 }} />
              <Space size={8}>
                <Tooltip title="Save">
                  <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    onClick={() => handleSaveAdd(node.data)}
                    style={{ background: '#1677ff', borderColor: '#1677ff', color: '#ffffff' }}
                  >
                    Save
                  </Button>
                </Tooltip>
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleCancelAdd}
                >
                  Cancel
                </Button>
              </Space>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: '#595959', display: 'block', marginBottom: 4, fontWeight: 500 }}>Name *</label>
                <Input
                  value={addFormData.name}
                  onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })}
                  placeholder="Enter product name"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 14, color: '#262626', display: 'block', marginBottom: 4, fontWeight: 400 }}>Description</label>
                <TextArea
                  rows={2}
                  value={addFormData.description}
                  onChange={(e) => setAddFormData({ ...addFormData, description: e.target.value })}
                  placeholder="Description (optional)"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 14, color: '#262626', display: 'block', marginBottom: 4, fontWeight: 400 }}>Tags</label>
                <Select
                  mode="tags"
                  value={addFormData.tags}
                  onChange={(tags) => setAddFormData({ ...addFormData, tags })}
                  placeholder="Tags (press Enter to add)"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleHierarchySync = () => {
    Modal.confirm({
      title: "Sync Products Overview",
      content: "This will sync the hierarchy with the products list. Continue?",
      okText: "Sync",
      cancelText: "Cancel",
      onOk: async () => {
        setSyncLoading(true);
        try {
          await new Promise((resolve, reject) => {
            dispatch(
              syncProductOverviewApi({
                params: { dry_run: false },
                successCallback: (response) => {
                  message.success(response?.message || "Sync completed");
                  loadHierarchy();
                  resolve(response);
                },
                failureCallback: (error) => {
                  console.log(error);
                  message.error("Sync failed");
                  reject(error);
                },
                finalCallback: () => setSyncLoading(false),
              })
            );
          });
        } catch (error) {
          // error handled in failureCallback
        }
      },
    });
  };

  // Expand all nodes recursively
  const expandAll = () => {
    const getAllKeys = (nodes, prefix = 'node') => {
      return nodes.reduce((acc, node, idx) => {
        const key = `${prefix}-${idx}`;
        acc.push(key);
        if (node.children && node.children.length > 0) {
          acc.push(...getAllKeys(node.children, key));
        }
        return acc;
      }, []);
    };
    setExpandedKeys(getAllKeys(hierarchyTreeData));
  };

  const collapseAll = () => {
    setExpandedKeys([]);
  };

  const truncateText = (text, max = 59) => {
    if (!text) return "";
    return text.length > max ? `${text.slice(0, max)}...` : text;
  };

  return (
    <Card className="dashboard-header products-card" style={{ marginBottom: "24px" }}>
      <div
        className="products-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 12 : 0,
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Title level={4} style={{ color: "#333", fontWeight: 700, margin: 0 }}>
            Products
          </Title>
          <Tooltip title={`${productsList.total} Products found for selected filters`}>
            <Tag
              color="blue"
              style={{
                margin: 0,
                borderRadius: 999,
                padding: "2px 10px",
                fontWeight: 600,
              }}
            >
              {productsList.total} Products Found
            </Tag>
          </Tooltip>
        </div>

        <Button
          type="primary"
          onClick={() => {
            setDrawerVisible(true);
            if (hierarchy.length === 0) {
              loadHierarchy();
            }
          }}
          icon={<UnorderedListOutlined />}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          Product Hierarchy
        </Button>
      </div>

      <div className="products-content">
        <div
          className="products-actions"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
          }}
        >
          <Input
            size="middle"
            className="products-search"
            placeholder="Search products"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            prefix={<span style={{ marginRight: 8 }}>{loading ? <Spin style={{ color: "#111" }} size="small" indicator={<LoadingOutlined />} /> : <SearchOutlined />}</span>}
            allowClear
            style={{ flex: 1, minWidth: isMobile ? "100%" : 280 }}
          />

          {selectedNode && (
            <Tooltip title={`Filtered by: ${formatHierarchyNodeName(selectedNode.name)}`}>
              <Tag
                color="processing"
                icon={<FilterOutlined />}
                closable
                onClose={(e) => {
                  e.preventDefault();
                  setSelectedNode(null);
                  setPageNumber(1);
                }}
                style={{
                  marginLeft: 8,
                  borderRadius: 6,
                  padding: "3px 10px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                }}
              >
                {truncateText(formatHierarchyNodeName(selectedNode.name), 59)}
              </Tag>
            </Tooltip>
          )}

          <Tooltip title="Refresh Data" placement="top">
            <Button
              className="products-refresh"
              type="text"
              color="primary"
              variant="outlined"
              icon={<ReloadOutlined />}
              size="medium"
              onClick={() => setRefreshTrigger((prev) => !prev)}
              style={{
                height: 32,
                width: isMobile ? "100%" : "auto",
              }}
            >
              Refresh
            </Button>
          </Tooltip>
        </div>

        <div className="products-table">
          <Table
            dataSource={productsList.products}
            columns={columns}
            rowKey={(item) => item._id}
            pagination={false}
            bordered={false}
            size={isMobile ? "small" : "middle"}
            scroll={{ x: isMobile ? 760 : 980 }}
            style={{
              width: "100%",
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              overflow: 'hidden'
            }}
          />
          <DataTablePagination
            totalDocuments={productsList.total}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setPageNumber={setPageNumber}
            currentPage={pageNumber}
            showTotal={(total) => `Total ${total} Products`}
          />
        </div>
      </div>

      <Modal
        title="Delete Hierarchy Item"
        open={deleteConfirmVisible}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{formatHierarchyNodeName(nodeToDelete?.name) || "this hierarchy item"}</strong>? This action cannot be undone.
        </p>
        <div style={{ marginTop: 12 }}>
          <Checkbox
            checked={deleteProductWithNode}
            onChange={(e) => setDeleteProductWithNode(e.target.checked)}
          >
            Also delete linked products from product list
          </Checkbox>
        </div>
      </Modal>

      <Modal
        title="Add Category"
        open={rootModalVisible}
        onOk={handleRootModalOk}
        onCancel={handleRootModalCancel}
        width={600}
        centered
        okText="Add"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter a category name" }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Enter description (optional)"
            />
          </Form.Item>

          <Form.Item name="tags" label="Tags">
            <Select
              mode="tags"
              placeholder="Enter tags (press Enter to add)"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={null}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={640}
        styles={{
          header: { display: 'none' },
          body: { padding: 0 },
        }}
      >
        <div className="hierarchy-drawer-container">
          {/* Drawer header */}
          <div className="hierarchy-drawer-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="hierarchy-drawer-icon">
                <AppstoreOutlined style={{ fontSize: 20, color: '#fff' }} />
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1f1f1f' }}>
                  Product Hierarchy
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                  Manage your product categories and items
                </div>
              </div>
            </div>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setDrawerVisible(false)}
              style={{ color: '#8c8c8c' }}
            />
          </div>

          {/* Toolbar */}
          <div className="hierarchy-drawer-toolbar">
            <Input
              placeholder="Search hierarchy..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={hierarchySearch}
              onChange={(e) => setHierarchySearch(e.target.value)}
              allowClear
              style={{ flex: 1 }}
              size="middle"
            />
            <Space size={6}>
              <Tooltip title="Expand all">
                <Button
                  size="small"
                  type="text"
                  onClick={expandAll}
                  style={{ fontSize: 12, color: '#595959' }}
                >
                  <iconify-icon icon="material-symbols:expand-all-rounded" width="16" height="16"></iconify-icon>
                </Button>
              </Tooltip>
              <Tooltip title="Collapse all">
                <Button
                  size="small"
                  type="text"
                  onClick={collapseAll}
                  style={{ fontSize: 12, color: '#595959' }}
                >
                  <iconify-icon icon="material-symbols:collapse-all-rounded" width="16" height="16"></iconify-icon>
                </Button>
              </Tooltip>
              <Button
                type="primary"
                size=""
                icon={<PlusOutlined />}
                onClick={() => setRootModalVisible(true)}
              >
                Category
              </Button>
              <Tooltip title="Sync hierarchy with products">
                <Button
                  size=""
                  icon={<ReloadOutlined />}
                  onClick={handleHierarchySync}
                  loading={syncLoading}
                  disabled={hierarchyLoading}
                >
                  Sync
                </Button>
              </Tooltip>
            </Space>
          </div>

          {/* Selected filter badge */}
          {/* {selectedNode && (
            <div className="hierarchy-drawer-filter-badge">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#595959' }}>Filtered by:</span>
                <Tag
                  color="blue"
                  closable
                  onClose={() => { setSelectedNode(null); setPageNumber(1); }}
                  style={{ margin: 0 }}
                >
                  {selectedNode.name}
                </Tag>
              </div>
            </div>
          )} */}

          {/* Tree content */}
          <div className="hierarchy-drawer-content" ref={hierarchyRef}>
            {hierarchyLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
                <Spin indicator={<LoadingOutlined spin />} />
              </div>
            ) : hierarchyTreeData.length === 0 ? (
              <div style={{ padding: '48px 0' }}>
                <Empty
                  description={
                    <div>
                      <div style={{ fontSize: 14, color: '#595959', marginBottom: 4 }}>No hierarchy data</div>
                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>Click "Category" to create your first category</div>
                    </div>
                  }
                />
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={hierarchyCollisionDetection}
                onDragStart={handleDragStart}
                onDragCancel={handleDragCancel}
                onDragEnd={handleDragEnd}
              >
                <DropZone
                  id="drop-root-end"
                  data={{ type: "drop", dropType: DROP_TYPE.ROOT_END }}
                  activeDragNodeId={activeDragNodeId}
                  style={{ borderRadius: 10, padding: 4, minHeight: "100%", height: "100%" }}
                >
                  <div className="hierarchy-tree-container" style={{ minHeight: "100%" }}>
                    {hierarchyTreeData.map((node, idx) =>
                      renderTreeNode(node, 0, idx === hierarchyTreeData.length - 1)
                    )}
                  </div>
                </DropZone>
                <DragOverlay dropAnimation={null}>
                  {activeDragPreview ? (
                    <div
                      style={{
                        minWidth: 280,
                        maxWidth: 420,
                        background: "#fff",
                        border: "1px solid #d9d9d9",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
                        padding: "9px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        pointerEvents: "none",
                      }}
                    >
                      {activeDragPreview.hasProductId ? (
                        <ShoppingOutlined style={{ fontSize: 14, color: "#1890ff" }} />
                      ) : activeDragPreview.hasChildren ? (
                        <FolderOutlined style={{ fontSize: 14, color: "#2f54eb" }} />
                      ) : (
                        <FileTextOutlined style={{ fontSize: 14, color: "#2f54eb" }} />
                      )}
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#1f1f1f",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {activeDragPreview.title}
                      </span>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        </div>
      </Drawer>
    </Card>
  );
};

export default ProductComponent;
